import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  CheckCircle2, Loader2, Car, TrendingUp, ShoppingCart, RotateCcw, Trophy,
  Edit3, Check, X as XIcon, Calendar, MapPin, Heart, Bell, LogOut, ExternalLink, Megaphone, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { relistVehicle } from '../lib/profile';
import { updateMyProfile } from '../lib/events';
import { markNotificationsRead } from '../lib/watchlist';
import VerifiedBadge from '../components/VerifiedBadge';
import PhotoUploader from '../components/PhotoUploader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(n) { return `$${(n || 0).toLocaleString()}`; }

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

const TABS = [
  { k: 'active',        label: 'Listings',      icon: Car },
  { k: 'sold',          label: 'Sold',          icon: Trophy },
  { k: 'watchlist',     label: 'Watchlist',     icon: Heart },
  { k: 'rsvps',         label: 'My events',     icon: Calendar },
  { k: 'notifications', label: 'Inbox',         icon: Bell },
];

export default function Profile() {
  const { user, loading: authLoading, setUser, logout } = useAuth();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [tab, setTab] = React.useState('active');

  const [editing, setEditing] = React.useState(false);
  const [bio, setBio] = React.useState('');
  const [name, setName] = React.useState('');
  const [picture, setPicture] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const { data: p } = await axios.get(`${API}/profile/me`, { withCredentials: true });
      setData(p);
      setBio(p.user.bio || '');
      setName(p.user.name || '');
      setPicture(p.user.picture || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load profile');
    } finally {
      setLoading(false);
    }
  }, [user]);

  React.useEffect(() => { if (user) load(); }, [user, load]);

  const saveProfile = async () => {
    try {
      setSaving(true);
      const updated = await updateMyProfile({ bio, name, picture: picture || null });
      setUser(updated);
      setData((prev) => ({ ...prev, user: updated }));
      setEditing(false);
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const markAllRead = async () => {
    if (!user?.email) return;
    try {
      await markNotificationsRead(user.email, null);
      toast.success('Inbox marked as read');
      load();
    } catch { toast.error('Could not mark as read'); }
  };

  const relist = async (id) => {
    try { await relistVehicle(id, user.email); toast.success('Re-listed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (authLoading || (user && loading)) {
    return <div className="min-h-[60vh] grid place-items-center" data-testid="profile-loading"><Loader2 className="w-8 h-8 text-[#ff2a2a] animate-spin" /></div>;
  }
  if (!user) return <Navigate to="/signin" replace />;
  if (!data) return null;

  const active = data.active_listings || [];
  const sold = data.sold_listings || [];
  const watchlist = data.watchlist || [];
  const rsvps = data.rsvps || [];
  const notifications = data.notifications || [];
  const stats = data.stats || {};
  const verified = stats.sold_count >= 3;

  const counts = {
    active: stats.active_count ?? active.length,
    sold: stats.sold_count ?? sold.length,
    watchlist: stats.watchlist_count ?? watchlist.length,
    rsvps: stats.rsvp_count ?? rsvps.length,
    notifications: stats.unread_notifications ?? 0,
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14" data-testid="page-profile">
      {/* HEADER */}
      <div className="flex items-start gap-5 flex-wrap">
        {(editing ? picture : user.picture) ? (
          <img src={editing ? picture : user.picture} alt={user.name} className="w-24 h-24 rounded-full object-cover hairline" />
        ) : (
          <div className="w-24 h-24 rounded-full bg-[#ff2a2a] text-white grid place-items-center text-4xl font-display">
            {(user.name || user.email)[0].toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-1 inline-flex items-center gap-2">
            My account{verified && <VerifiedBadge />}
          </div>
          {editing ? (
            <input value={name} onChange={(e)=>setName(e.target.value)} className="w-full bg-[#0d0d0d] hairline px-3 py-2 font-display text-3xl uppercase tracking-tight text-white focus:outline-none focus:border-[#ff2a2a]" placeholder="Your name" data-testid="edit-name"/>
          ) : (
            <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none truncate" data-testid="profile-name">{user.name || user.email.split('@')[0]}</h1>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-zinc-400" data-testid="profile-email-private">
            <Lock className="w-3.5 h-3.5 text-[#ff2a2a]" />
            <span data-testid="profile-email">{user.email}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 border border-zinc-800 px-1.5 py-0.5" title="Your email is never shown on your public profile, listings or events">Private · only you</span>
          </div>

          {editing ? (
            <>
              <textarea rows={3} value={bio} onChange={(e) => setBio(e.target.value.slice(0, 500))} placeholder="Tell buyers about yourself — years in the scene, what you drive, what you're into…" className="mt-3 w-full bg-[#0d0d0d] hairline px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]" data-testid="edit-bio" />
              <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-600 flex items-center justify-between"><span>{bio.length} / 500</span><span>Upload an avatar below</span></div>
              <div className="mt-3 max-w-xs"><PhotoUploader value={picture ? [picture] : []} onChange={(v) => setPicture(v[v.length - 1] || '')} /></div>
              <div className="mt-4 flex gap-2">
                <button onClick={saveProfile} disabled={saving} className="bg-[#ff2a2a] hover:bg-[#e01f1f] disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 inline-flex items-center gap-2" data-testid="save-profile">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Check className="w-4 h-4"/>} Save
                </button>
                <button onClick={() => { setEditing(false); setBio(user.bio || ''); setName(user.name || ''); setPicture(user.picture || ''); }} className="hairline hover:border-white/30 text-zinc-300 text-sm font-semibold px-4 py-2 inline-flex items-center gap-2" data-testid="cancel-edit"><XIcon className="w-4 h-4"/> Cancel</button>
              </div>
            </>
          ) : (
            <>
              {user.bio ? (
                <p className="mt-3 text-sm text-zinc-300 max-w-2xl whitespace-pre-wrap" data-testid="profile-bio">{user.bio}</p>
              ) : (
                <p className="mt-3 text-sm text-zinc-600 italic">Add a bio so buyers know who they&rsquo;re dealing with.</p>
              )}
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button onClick={() => setEditing(true)} className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-[#ff2a2a] transition-colors inline-flex items-center gap-1" data-testid="edit-profile">
                  <Edit3 className="w-3 h-3"/> Edit profile
                </button>
                <Link to={`/users/${user.user_id}`} className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-[#ff2a2a] transition-colors inline-flex items-center gap-1" data-testid="view-public-profile">
                  View public profile <ExternalLink className="w-3 h-3"/>
                </Link>
                <button onClick={logout} className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-[#ff2a2a] transition-colors inline-flex items-center gap-1" data-testid="signout-btn">
                  <LogOut className="w-3 h-3"/> Sign out
                </button>
              </div>
              {!verified && (
                <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  {Math.max(3 - counts.sold, 0)} more sale{3 - counts.sold === 1 ? '' : 's'} to earn <span className="text-emerald-400">Verified Seller</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="mt-10 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="profile-stats">
        <StatCard icon={Car}      label="Active"     value={counts.active} />
        <StatCard icon={Trophy}   label="Sold"       value={counts.sold} />
        <StatCard icon={Heart}    label="Watchlist"  value={counts.watchlist} />
        <StatCard icon={Calendar} label="Events"     value={counts.rsvps} />
        <StatCard icon={Bell}     label="Unread"     value={counts.notifications} highlight={counts.notifications > 0} />
      </div>

      {/* TABS */}
      <div className="mt-10 flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex flex-wrap hairline p-1" data-testid="account-tabs" role="tablist">
          {TABS.map(({ k, label, icon: Icon }) => {
            const c = counts[k];
            return (
              <button
                key={k}
                role="tab"
                aria-selected={tab === k}
                onClick={() => setTab(k)}
                data-testid={`tab-${k}`}
                className={`px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors inline-flex items-center gap-1.5 ${
                  tab === k ? 'bg-[#ff2a2a] text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5"/>
                {label}
                {c > 0 && <span className={`ml-1 text-[10px] ${tab === k ? 'text-white/80' : 'text-zinc-500'}`}>· {c}</span>}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Link to="/host-event" className="hairline hover:border-[#ff2a2a] text-white text-xs font-mono uppercase tracking-widest px-3 py-2 inline-flex items-center gap-1.5" data-testid="profile-host-cta"><Calendar className="w-3.5 h-3.5"/> Host event</Link>
          <Link to="/sell" className="hairline hover:border-[#ff2a2a] text-white text-xs font-mono uppercase tracking-widest px-3 py-2 inline-flex items-center gap-1.5" data-testid="profile-sell-cta"><ShoppingCart className="w-3.5 h-3.5"/> List vehicle</Link>
        </div>
      </div>

      {/* TAB CONTENT */}
      <div className="mt-8">
        {tab === 'active' && (
          <ListingsGrid items={active} emptyTitle="No active listings" emptyBody="When you list a vehicle, it shows up here." cta={{ to: '/sell', label: 'List your first vehicle' }} onRelist={relist} />
        )}
        {tab === 'sold' && (
          <ListingsGrid items={sold} emptyTitle="No sales yet" emptyBody="Mark a listing as SOLD to build sales history and earn Verified." cta={{ to: '/sell', label: 'List a vehicle' }} onRelist={relist} />
        )}
        {tab === 'watchlist' && (
          <WatchlistGrid items={watchlist} />
        )}
        {tab === 'rsvps' && (
          <RsvpGrid items={rsvps} myUserId={user.user_id} />
        )}
        {tab === 'notifications' && (
          <NotificationsList items={notifications} onMarkAllRead={markAllRead} unread={counts.notifications} />
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`am-card p-5 ${highlight ? 'ring-1 ring-[#ff2a2a]' : ''}`}>
      <Icon className={`w-5 h-5 ${highlight ? 'text-[#ff2a2a]' : 'text-[#ff2a2a]'}`} />
      <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display text-3xl text-white">{value ?? 0}</div>
    </div>
  );
}

function EmptyCard({ title, body, cta }) {
  return (
    <div className="am-card p-16 text-center" data-testid="listings-empty">
      <Car className="w-10 h-10 mx-auto text-zinc-700" />
      <div className="mt-4 font-display text-2xl uppercase tracking-tight">{title}</div>
      <p className="mt-2 text-zinc-400 text-sm max-w-md mx-auto">{body}</p>
      {cta && <Link to={cta.to} className="mt-6 inline-block bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-6 py-3 transition-colors">{cta.label}</Link>}
    </div>
  );
}

function ListingsGrid({ items, emptyTitle, emptyBody, cta, onRelist }) {
  if (!items.length) return <EmptyCard title={emptyTitle} body={emptyBody} cta={cta} />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="listings-grid">
      {items.map((v) => (
        <article key={v.id} className="am-card overflow-hidden flex flex-col relative" data-testid={`profile-listing-${v.id}`}>
          {v.status === 'sold' && <div className="absolute top-3 left-3 z-10 bg-emerald-500 text-black px-2 py-1 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> SOLD</div>}
          <Link to={`/vehicles/${v.id}`} className="block relative aspect-[4/3] overflow-hidden">
            {v.photos && v.photos[0] ? <img src={v.photos[0]} alt={v.title} className={`w-full h-full object-cover ${v.status === 'sold' ? 'grayscale opacity-70' : ''}`} /> : <div className="w-full h-full bg-[#131313] grid place-items-center"><Car className="w-8 h-8 text-zinc-600"/></div>}
            <div className="absolute bottom-3 right-3 font-mono text-lg text-white bg-black/60 px-2.5 py-1 backdrop-blur-md">{money(v.price)}</div>
          </Link>
          <div className="p-4">
            <Link to={`/vehicles/${v.id}`} className="text-white font-semibold hover:text-[#ff2a2a] transition-colors">{v.title}</Link>
            {v.status === 'sold' && v.sold_at && <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-emerald-400">Sold {new Date(v.sold_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}</div>}
            {v.status === 'sold' && <button onClick={() => onRelist(v.id)} className="mt-3 hairline hover:border-[#ff2a2a] hover:text-white text-zinc-300 text-xs font-semibold py-2 transition-colors inline-flex items-center justify-center gap-1.5 w-full" data-testid={`relist-${v.id}`}><RotateCcw className="w-3 h-3" /> Re-list</button>}
          </div>
        </article>
      ))}
    </div>
  );
}

function WatchlistGrid({ items }) {
  if (!items.length) return (
    <EmptyCard
      title="Nothing saved yet"
      body="Tap the heart on any listing to keep it in your watchlist and get price-drop alerts."
      cta={{ to: '/marketplace', label: 'Browse marketplace' }}
    />
  );
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="watchlist-grid">
      {items.map((v) => (
        <Link to={`/vehicles/${v.id}`} key={v.id} className="am-card overflow-hidden flex flex-col group relative" data-testid={`watchlist-item-${v.id}`}>
          {v.price_drop && (
            <div className="absolute top-3 left-3 z-10 bg-[#ff2a2a] text-white px-2 py-1 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> −{money(v.price_drop.delta)}
            </div>
          )}
          <div className="relative aspect-[4/3] overflow-hidden">
            {v.photos && v.photos[0] ? (
              <img src={v.photos[0]} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
            ) : <div className="w-full h-full bg-[#131313] grid place-items-center"><Car className="w-8 h-8 text-zinc-600"/></div>}
            <div className="absolute bottom-3 right-3 font-mono text-lg text-white bg-black/60 px-2.5 py-1 backdrop-blur-md">{money(v.price)}</div>
          </div>
          <div className="p-4">
            <div className="text-white font-semibold group-hover:text-[#ff2a2a] transition-colors">{v.title}</div>
            {v.price_drop && (
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#ff2a2a]">
                Was {money(v.price_drop.old_price)} · dropped {fmtDate(v.price_drop.at)}
              </div>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
}

function RsvpGrid({ items, myUserId }) {
  if (!items.length) return (
    <EmptyCard
      title="No events on your calendar"
      body="RSVP to a meet, cars & coffee, or track day and it'll show up here."
      cta={{ to: '/events', label: 'Browse events' }}
    />
  );
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rsvps-grid">
      {items.map((e) => (
        <article key={e.id} className="am-card overflow-hidden flex flex-col" data-testid={`rsvp-item-${e.id}`}>
          <div className="relative aspect-[16/10] bg-[#131313]">
            {e.img ? <img src={e.img} alt={e.title} className="w-full h-full object-cover"/> : <div className="w-full h-full grid place-items-center text-zinc-700"><Car className="w-10 h-10"/></div>}
            <div className="absolute top-3 left-3 am-pill bg-black/60 backdrop-blur-md">{e.category}</div>
            <div className="absolute bottom-3 left-3 bg-[#ff2a2a] text-white px-3 py-2">
              <div className="font-mono text-[10px] uppercase tracking-widest">{new Date(e.date).toLocaleDateString('en-CA',{month:'short'})}</div>
              <div className="font-display text-2xl leading-none">{new Date(e.date).getDate()}</div>
            </div>
            {e.host_user_id === myUserId && (
              <div className="absolute top-3 right-3 am-pill bg-black/60 backdrop-blur-md inline-flex items-center gap-1 text-[10px]">
                <Megaphone className="w-3 h-3 text-[#ff2a2a]"/> Hosting
              </div>
            )}
          </div>
          <div className="p-4 flex-1 flex flex-col">
            <div className="text-white font-semibold">{e.title}</div>
            <div className="mt-2 space-y-1.5 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
              <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3"/>{e.date}{e.time ? ` · ${e.time}` : ''}</div>
              {(e.venue || e.city) && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3"/>{[e.venue, e.city].filter(Boolean).join(', ')}</div>}
              <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400"/> Going · {e.attendees ?? 0}</div>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

function NotificationsList({ items, onMarkAllRead, unread }) {
  if (!items.length) return (
    <EmptyCard
      title="Inbox is empty"
      body="You'll see price-drop alerts and host broadcasts here."
    />
  );
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Latest 20 · {unread} unread</div>
        {unread > 0 && (
          <button onClick={onMarkAllRead} className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-[#ff2a2a] transition-colors inline-flex items-center gap-1.5" data-testid="mark-all-read">
            <Check className="w-3 h-3"/> Mark all read
          </button>
        )}
      </div>
      <div className="space-y-2" data-testid="notifications-list">
        {items.map((n) => <NotificationRow key={n.id} n={n} />)}
      </div>
    </div>
  );
}

function NotificationRow({ n }) {
  const isUnread = !n.read;
  if (n.kind === 'price_drop') {
    return (
      <Link to={`/vehicles/${n.vehicle_id}`} className={`am-card p-4 flex items-start gap-3 hover:border-[#ff2a2a] transition-colors ${isUnread ? 'bg-[#131313]' : ''}`} data-testid={`notif-${n.id}`}>
        <div className="w-9 h-9 grid place-items-center bg-[#ff2a2a]/10 text-[#ff2a2a] shrink-0"><TrendingUp className="w-4 h-4"/></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white">
            <span className="font-semibold">Price drop</span> · {money(n.old_price)} → <span className="text-emerald-400">{money(n.new_price)}</span>
          </div>
          <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">{fmtDate(n.created_at)}</div>
        </div>
        {isUnread && <span className="w-2 h-2 rounded-full bg-[#ff2a2a] mt-2 shrink-0" aria-label="unread" />}
      </Link>
    );
  }
  if (n.kind === 'event_broadcast') {
    return (
      <div className={`am-card p-4 flex items-start gap-3 ${isUnread ? 'bg-[#131313]' : ''}`} data-testid={`notif-${n.id}`}>
        <div className="w-9 h-9 grid place-items-center bg-[#ff2a2a]/10 text-[#ff2a2a] shrink-0"><Megaphone className="w-4 h-4"/></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white">
            <span className="font-semibold">{n.from_name}</span> · <span className="text-zinc-400">{n.event_title}</span>
          </div>
          <p className="mt-1 text-sm text-zinc-300 whitespace-pre-wrap">{n.message}</p>
          <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">{fmtDate(n.created_at)}</div>
        </div>
        {isUnread && <span className="w-2 h-2 rounded-full bg-[#ff2a2a] mt-2 shrink-0" aria-label="unread" />}
      </div>
    );
  }
  return (
    <div className="am-card p-4 text-sm text-zinc-300" data-testid={`notif-${n.id}`}>
      <div className="font-semibold">{n.kind}</div>
      <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">{fmtDate(n.created_at)}</div>
    </div>
  );
}
